import React, { useEffect, createContext, useState, useCallback } from 'react';
import { IStyle } from 'Styles';
import {
  ITreeNodeAccessRightDTO,
  ITreeNodeAccessRightRequestDTO,
  TreeNodeAccessRightDTO,
} from 'business/models/Models';
import { TreeService } from 'business/services/TreeService';
import { getCurrentUser, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import UserGroupWithAccess from './userGroupWithAccess';
import UserGroupWithoutAccess from './userGroupWithoutAccess';
import { FormActionButton } from 'components/Button';
import { AccessRightType } from 'constants/';

interface Props {
  treeNodeId: number;
  treeNodeType: string;
  nodeTitle: string;
  closeModal: () => void;
  onSave?: (treeNodeId: number, payload: ITreeNodeAccessRightDTO[]) => void;
}

export interface NodeAccessRightsType {
  data: ITreeNodeAccessRightRequestDTO;
  isFetching?: boolean;
}

const userGroupDefaultState: NodeAccessRightsType = { data: null, isFetching: false };
export const NodeAccessRightsContext = createContext(null);

const treeService = new TreeService();

const AccessRight = (props: Props) => {
  const { treeNodeId, nodeTitle, closeModal, onSave } = props;
  const [state, setState] = useState<NodeAccessRightsType>(userGroupDefaultState);

  const currentUser = getCurrentUser();

  const _fetchTreeNodeAccessRights = useCallback(() => {
    setState((prv) => ({ ...prv, isFetching: true }));
    treeService
      .getTreeNodeAccessRights(treeNodeId)
      .then((data: ITreeNodeAccessRightRequestDTO) => {
        setState({ data, isFetching: false });
      });
  }, [treeNodeId]);

  const handleOnSaveClick = useCallback(() => {
    const payload: ITreeNodeAccessRightDTO[] = state.data.withAccessRights.map(
      (row: TreeNodeAccessRightDTO) => ({
        accessRight: row.accessRight.length
          ? row.accessRight.map((ar) => ({
              id: ar.id,
              init: null,
              toJSON: null,
            }))
          : [{ id: AccessRightType.VISIBLE, init: null, toJSON: null }],
        [row.user?.email ? 'user' : 'group']: {
          id: row.user?.email ? row.user.id : row.group.id,
        },
        init: null,
        toJSON: null,
      }),
    );
    if (onSave) {
      onSave(treeNodeId, payload);
      closeModal();
      return;
    }
    treeService
      .updateTreeNodeAccessRights(treeNodeId, payload as TreeNodeAccessRightDTO[])
      .then(() => {
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          'Your Changes Have been saved',
        );
        closeModal();
      })
      .catch((e) => {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          e?.message ?? 'Failed to update access rights.',
        );
      });
  }, [state, treeNodeId, onSave, closeModal]);

  useEffect(() => {
    _fetchTreeNodeAccessRights();
  }, []);

  return (
    <div id="accessright-index-wrapper">
      {state.isFetching || !state.data ? (
        <div id="accessright-index-loading-message">loading....</div>
      ) : (
        <div id="accessright-index-usergroup-wrapper" style={styles.root}>
          <div
            id="accessright-index-usergroup-withaccess"
            style={styles.withAccessSection}
          >
            <UserGroupWithAccess
              state={state}
              setState={setState}
              nodeTitle={nodeTitle}
              currentUser={currentUser}
            />
          </div>
          <div
            id="accessright-index-usergroup-withoutaccess"
            style={styles.withoutAccessSection}
          >
            <UserGroupWithoutAccess
              state={state}
              setState={setState}
              nodeTitle={nodeTitle}
            />
          </div>
        </div>
      )}
      <div id="accessright-index-form-action-button" style={styles.bottomActionButtons}>
        <FormActionButton onSaveClick={handleOnSaveClick} onDiscardClick={closeModal} />
      </div>
    </div>
  );
};

const styles: IStyle = {
  root: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    display: 'flex',
    backgroundColor: 'white',
  },
  withAccessSection: {
    width: '60%',
    marginRight: '1%',
    border: '1px dashed #ccc',
    marginBottom: '1%',
  },
  withoutAccessSection: {
    width: '40%',
    border: '1px dashed #ccc',
    marginBottom: '1%',
  },
  bottomActionButtons: {
    marginBottom: '1%',
  },
};
export default AccessRight;
